import React, { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useAuth } from "../../utils/context/AuthContext"
import { useAccessChecker } from "../../utils/Functions/Authorization"
import log from "../../assets/images/logo.png"

import {
  ShoppingCart,
  Tag,
  Package,
  FileText,
  Users,
  Gear,
  ShieldCheck,
  SignOut,
  CaretLeft,
  CaretRight,
  Star,
  Megaphone,
  BracketsCurly, // ✅ Content
} from "phosphor-react"

function cn(...classes) {
  return classes.filter(Boolean).join(" ")
}

export default function Sidebar({ isOpen, setIsOpen }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { checkAccess } = useAccessChecker()

  const [isMobile, setIsMobile] = useState(false)
  const wasMobileRef = useRef(false)

  const mainNavItems = useMemo(
    () => [
      { title: "Categories", path: "/categories", icon: Tag, access: checkAccess("categories", "read") },
      { title: "Brands", path: "/Brands", icon: ShieldCheck, access: checkAccess("brands", "read") },
      { title: "Products", path: "/", icon: Package, access: checkAccess("products", "read") },
      { title: "Orders", path: "/orders", icon: ShoppingCart, access: checkAccess("orders", "read") },
      { title: "Contacts", path: "/Contacts", icon: Users, access: checkAccess("contacts", "read") },
      { title: "Blogs", path: "/blogs", icon: FileText, access: checkAccess("blogs", "read") },
      { title: "Reviews", path: "/reviews", icon: Star, access: checkAccess("reviews", "read") },

      // ✅ Content (Strapi-like)
      { title: "Content", path: "/content", icon: BracketsCurly, access: checkAccess("pixels", "read") },
    ],
    [checkAccess],
  )

  const toolsNavItems = useMemo(
    () => [
      { title: "User Management", path: "/users", icon: Users, access: checkAccess("users", "read") },
      { title: "Roles", path: "/roles", icon: ShieldCheck, access: checkAccess("roles", "read") },
      { title: "Settings", path: "/settings", icon: Gear, access: checkAccess("settings", "read") },
    ],
    [checkAccess],
  )

  // ✅ Fix: don’t force open/close on every resize (keeps your manual toggle on desktop)
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 868
      setIsMobile(mobile)

      // entering mobile => close
      if (mobile && !wasMobileRef.current) setIsOpen(false)

      // leaving mobile => open (nice default)
      if (!mobile && wasMobileRef.current) setIsOpen(true)

      wasMobileRef.current = mobile
    }

    onResize()
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [setIsOpen])

  const toggleSidebar = () => setIsOpen((v) => !v)

  const handleLogOut = async (e) => {
    e.preventDefault()
    try {
      await logout?.()
    } finally {
      navigate("/login")
    }
  }

  const isActive = (path) => {
    const current = (location.pathname || "").toLowerCase()
    const p = (path || "").toLowerCase()
    if (p === "/") return current === "/"
    return current === p || current.startsWith(p + "/")
  }

  return (
    <>
      {/* mobile overlay */}
      {isMobile && isOpen && (
        <div className="fixed inset-0 z-30 bg-black/50" onClick={toggleSidebar} aria-hidden="true" />
      )}

      <aside
        className={cn(
          "fixed left-0 top-0 z-40 h-full flex flex-col bg-neutral-950 transition-all duration-300 ease-in-out",
          isOpen ? "w-64" : "w-20",
          isMobile && !isOpen ? "-translate-x-full" : "translate-x-0",
        )}
      >
        {/* Header */}
        <div
          className={cn(
            "relative py-6 flex items-center",
            isOpen ? "justify-between px-6" : "justify-center px-3",
          )}
        >
          <Link to="/" className="flex items-center gap-2 rounded-lg">
            {isOpen ? (
              <img src={log} alt="Logo" className="h-11 w-auto" draggable={false} />
            ) : (
             <></>
            )}
          </Link>

          {/* Toggle */}
          <button
            onClick={toggleSidebar}
            className={cn(
              "h-9 w-9 rounded-full grid place-items-center transition",
              "bg-cyan-400 text-white hover:bg-cyan-300",
              isOpen && !isMobile ? "absolute -right-4 shadow-lg" : "",
            )}
            aria-label={isOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            {isOpen ? <CaretLeft size={18} weight="bold" /> : <CaretRight size={18} weight="bold" />}
          </button>
        </div>

        {/* Nav */}
        <div className={cn("flex-1 overflow-y-auto py-4", isOpen ? "px-3" : "px-2")}>
          <nav className={cn("space-y-1", isOpen ? "px-2" : "px-1")}>
            {mainNavItems
              .filter((i) => !!i.access)
              .map((item) => {
                const active = isActive(item.path)
                const Icon = item.icon

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "group flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-[400] transition",
                      isOpen ? "" : "justify-center",
                      active ? "bg-cyan-400 text-white" : "text-blue-100 hover:bg-cyan-400/20 hover:text-white",
                    )}
                    title={!isOpen ? item.title : undefined}
                    onClick={() => {
                      // ✅ auto close after click on mobile
                      if (isMobile) setIsOpen(false)
                    }}
                  >
                    <Icon size={20} weight={active ? "fill" : "regular"} className="shrink-0" />

                    {isOpen && (
                      <div className="flex items-center justify-between w-full min-w-0">
                        <span className="truncate">{item.title}</span>

                        {!!item.badge && (
                          <span
                            className={cn(
                              "ml-3 shrink-0 rounded-full px-2 py-0.5 text-[11px] font-[600]",
                              active ? "bg-white/25 text-white" : "bg-white/10 text-blue-100",
                            )}
                          >
                            {item.badge}
                          </span>
                        )}
                      </div>
                    )}
                  </Link>
                )
              })}
          </nav>

          {/* Tools */}
          <div className="mt-8">
            {isOpen && (
              <div className="px-5 text-xs text-start font-[600] tracking-widest text-cyan-400">TOOLS</div>
            )}

            <nav className={cn("mt-2 space-y-1", isOpen ? "px-2" : "px-1")}>
              {toolsNavItems
                .filter((i) => !!i.access)
                .map((item) => {
                  const active = isActive(item.path)
                  const Icon = item.icon

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cn(
                        "group flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-[400] transition",
                        isOpen ? "" : "justify-center",
                        active ? "bg-cyan-400 text-white" : "text-blue-100 hover:bg-cyan-400/20 hover:text-white",
                      )}
                      title={!isOpen ? item.title : undefined}
                      onClick={() => {
                        if (isMobile) setIsOpen(false)
                      }}
                    >
                      <Icon size={20} weight={active ? "fill" : "regular"} className="shrink-0" />
                      {isOpen && <span className="truncate">{item.title}</span>}
                    </Link>
                  )
                })}
            </nav>
          </div>
        </div>

        {/* Footer */}
        <div className={cn("py-4", isOpen ? "px-5" : "px-3")}>
          <button
            onClick={handleLogOut}
            className={cn(
              "w-full flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-[400] transition",
              isOpen ? "" : "justify-center",
              "text-blue-100 hover:bg-cyan-400/20 hover:text-white",
            )}
            title={!isOpen ? "Log Out" : undefined}
          >
            <SignOut size={20} className="shrink-0" />
            {isOpen && <span>Log Out</span>}
          </button>
        </div>
      </aside>
    </>
  )
}
